// AddressForm — shipping/billing address entry that persists via the
// address API (POST /api/v1/marketplace/me/addresses).
//
// On valid submit: calls createAddress → on 201, invokes onAddressCreated
// with the server-returned address id (number). On API validation error
// (400), renders inline error without invoking the callback.
//
// Wire shape contract (§39): fields match Go handler's body struct
// (address_type, is_default, line1, line2, city, state, postal_code,
// country_code). No business_name, no label — those are phantom fields
// that don't exist on the Go model.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens(); composes
// embed-shared primitives (EmbedField/EmbedInput/EmbedSelect/EmbedButton).
//
// RESTYLE ONLY: the field set, client + API validation, the createAddress
// wiring, and the wire shape are unchanged — only the presentation layer
// was migrated onto the shared primitives + theme tokens.
//
// EDIT MODE (T5, My Account C1 AC3): when `editingAddress` is passed, the
// form pre-fills from it and PATCHes via updateAddress instead of POSTing a
// new address via createAddress — reusing this exact field set/markup
// rather than a second address form (design.md AC3 directive). All new
// behavior is gated behind the optional `editingAddress` prop, so every
// existing create-mode call site/test is unaffected. address_type is NOT
// user-editable in edit mode (out of scope) — the heading and PATCH body
// both use editingAddress.address_type, ignoring `variant` when set.
//
// COUNTRY (payment-capture design §1.7 G1/G2 — the blocking gap). Until now
// this form hardcoded `country_code: "US"` on create and OMITTED the field on
// update, so a non-US address could be neither created nor edited into
// existence through the customer UI. That made the ENTIRE international
// branch of the payment design unreachable: the geo authority is the billing
// address's country (§1.1), and no customer could ever set it to anything but
// US. The country control below is that gap closed — it is the prerequisite
// for card capture, not a cosmetic addition, and `country_code` now travels on
// BOTH the create and the update body.
//
// The country selection also drives the STATE control's shape. `state` is
// `VARCHAR(10) NOT NULL` and required by the handler for every country (§1.7
// G3), which is a US-shaped assumption the server has not yet relaxed — so
// outside the US this renders a free-text region field with the server's own
// 10-character ceiling surfaced as help text rather than discovered as a 400.
//
// ⚠️ AMENDED 2026-10-09 (account-area redesign, Task 21 BASELINE — F13's
// provider half is a separate gated dispatch). Every address input now carries
// its WHATWG autofill token (address-line1/address-line2/address-level2/
// address-level1/postal-code/country) plus a `name` attribute mirroring the
// wire field key it already submits. The token is the contract the later
// provider combobox fills fields BY; browsers already honor it for ordinary
// autofill. No values, validation or wire shape change — attributes only.

import { useState, useCallback } from "react";
import {
  useEmbedTokens,
  EmbedField,
  EmbedInput,
  EmbedSelect,
  EmbedButton,
  EmbedBanner,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { createAddress, updateAddress } from "./address-api";
import type { AddressWire } from "./address-api";
import { COUNTRY_OPTIONS, DEFAULT_COUNTRY_CODE, usesUsStateList } from "./country-options";

/** Server ceiling on `state` (customer_handler.go: "state is too long"). */
const STATE_MAX_LEN = 10;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export interface AddressFormProps {
  /** "shipping" or "billing" — sets the heading and address_type sent to API.
   *  Ignored when `editingAddress` is set (its own address_type wins). */
  variant: "shipping" | "billing";
  /** Called with the server-returned address id AND full address after
   *  successful persist (create OR edit — one callback name covers both
   *  modes so callers don't need a second prop). Passing the full object
   *  avoids replica-lag re-fetch (project rule #3). */
  onAddressCreated: (addressId: number, address: AddressWire) => void;
  /** Called when the user clicks "Back" (create mode) / "Cancel" (edit mode). */
  onBack?: () => void;
  /** EDIT MODE: when set, fields pre-fill from this address and submit calls
   *  updateAddress (PATCH, with this address's id + version) instead of
   *  createAddress (POST). See file-header comment for the full contract. */
  editingAddress?: AddressWire;
}

interface FieldState {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
}

export function AddressForm({ variant, onAddressCreated, onBack, editingAddress }: AddressFormProps) {
  const t = useEmbedTokens();
  const isEditing = !!editingAddress;
  // In edit mode the record's own address_type wins over the `variant` prop
  // (callers pass the address's current type; we don't offer a type switch).
  const effectiveVariant: "shipping" | "billing" = editingAddress
    ? (editingAddress.address_type === "billing" ? "billing" : "shipping")
    : variant;
  const [fields, setFields] = useState<FieldState>(() =>
    editingAddress
      ? {
          line1: editingAddress.line1,
          line2: editingAddress.line2 ?? "",
          city: editingAddress.city,
          state: editingAddress.state,
          postal_code: editingAddress.postal_code,
          country_code: editingAddress.country_code || DEFAULT_COUNTRY_CODE,
        }
      : { line1: "", line2: "", city: "", state: "", postal_code: "", country_code: DEFAULT_COUNTRY_CODE },
  );
  const isDomestic = usesUsStateList(fields.country_code);
  const [errors, setErrors] = useState<Partial<Record<keyof FieldState, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateField = useCallback((key: keyof FieldState, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (apiError) setApiError(null);
  }, [apiError]);

  // Changing country between the US and anywhere else swaps the state control
  // between a 50-state dropdown and a free-text region field. The previous
  // value is cleared across that swap deliberately: "CA" selected as
  // California must not survive into Canada as a region abbreviation, and a
  // free-text region must not survive into the dropdown as an unselectable
  // value that renders blank while still submitting.
  const updateCountry = useCallback((next: string) => {
    setFields((prev) => {
      if (prev.country_code === next) return prev;
      const vocabularyChanged = usesUsStateList(prev.country_code) !== usesUsStateList(next);
      return { ...prev, country_code: next, state: vocabularyChanged ? "" : prev.state };
    });
    setErrors((prev) => {
      if (!prev.state && !prev.country_code) return prev;
      const cleared = { ...prev };
      delete cleared.state;
      delete cleared.country_code;
      return cleared;
    });
    if (apiError) setApiError(null);
  }, [apiError]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Client-side validation.
      const errs: Partial<Record<keyof FieldState, string>> = {};
      const domestic = usesUsStateList(fields.country_code);
      if (!fields.line1.trim()) errs.line1 = "Address line 1 is required.";
      if (!fields.city.trim()) errs.city = "City is required.";
      if (!fields.country_code.trim()) errs.country_code = "Country is required.";
      // `state` is required by the handler for EVERY country, not just the US
      // (§1.7 G3). Saying so here beats a 400 the customer cannot act on.
      if (!fields.state.trim()) {
        errs.state = domestic ? "State is required." : "State / province / region is required.";
      } else if (fields.state.trim().length > STATE_MAX_LEN) {
        errs.state = `State / province / region must be ${STATE_MAX_LEN} characters or fewer.`;
      }
      if (!fields.postal_code.trim()) {
        errs.postal_code = domestic ? "ZIP code is required." : "Postal code is required.";
      }
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }

      setSubmitting(true);
      setApiError(null);

      try {
        const result = editingAddress
          ? await updateAddress(editingAddress.id, {
              version: editingAddress.version,
              line1: fields.line1.trim(),
              line2: fields.line2.trim() || null,
              city: fields.city.trim(),
              state: fields.state.trim(),
              postal_code: fields.postal_code.trim(),
              // §1.7 G2: the update path used to omit country_code even though
              // the API accepts it, so an address's country could never be
              // corrected once wrong. It travels now.
              country_code: fields.country_code,
            })
          : await createAddress({
              address_type: variant,
              is_default: false,
              line1: fields.line1.trim(),
              line2: fields.line2.trim() || null,
              city: fields.city.trim(),
              state: fields.state.trim(),
              postal_code: fields.postal_code.trim(),
              country_code: fields.country_code,
            });
        onAddressCreated(result.id, result);
      } catch (err) {
        if (err instanceof ApiError) {
          setApiError(err.message);
        } else {
          setApiError("Failed to save address. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [fields, variant, editingAddress, onAddressCreated],
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <h3
        style={{
          fontSize: t.fontSize.lg,
          fontWeight: 600,
          color: t.colors.text,
          margin: `0 0 ${t.spacing.md}px 0`,
        }}
      >
        {isEditing ? "Edit " : ""}
        {effectiveVariant === "shipping" ? "Shipping address" : "Billing address"}
      </h3>

      {apiError && (
        <div style={{ marginBottom: t.spacing.md }}>
          <EmbedBanner tone="error">{apiError}</EmbedBanner>
        </div>
      )}

      <EmbedField label="Address line 1" htmlFor={`${effectiveVariant}-line1`} required error={errors.line1}>
        <EmbedInput
          id={`${effectiveVariant}-line1`}
          name="line1"
          autoComplete="address-line1"
          type="text"
          value={fields.line1}
          onChange={(e) => updateField("line1", e.target.value)}
          aria-required="true"
          invalid={!!errors.line1}
          aria-invalid={!!errors.line1}
        />
      </EmbedField>

      <EmbedField label="Address line 2" htmlFor={`${effectiveVariant}-line2`}>
        <EmbedInput
          id={`${effectiveVariant}-line2`}
          name="line2"
          autoComplete="address-line2"
          type="text"
          value={fields.line2}
          onChange={(e) => updateField("line2", e.target.value)}
          placeholder="Apt, suite, etc."
        />
      </EmbedField>

      <EmbedField label="Country" htmlFor={`${effectiveVariant}-country`} required error={errors.country_code}>
        <EmbedSelect
          id={`${effectiveVariant}-country`}
          name="country_code"
          value={fields.country_code}
          onChange={(e) => updateCountry(e.target.value)}
          aria-required="true"
          invalid={!!errors.country_code}
          aria-invalid={!!errors.country_code}
          autoComplete="country"
        >
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </EmbedSelect>
      </EmbedField>

      {/* Task 8 (account-area redesign, 2026-10-02): City/State/ZIP share
          ONE three-column row — the ONLY layout change this task makes to
          this form. Everything else (labels, ids, the edit-mode heading,
          the action labels, the submit contract) is FROZEN:
          AddressSelector's checkout fallback consumes this component
          byte-identically (AddressSelector.test.tsx pins that). */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: t.spacing.md }}>
        <EmbedField label="City" htmlFor={`${effectiveVariant}-city`} required error={errors.city}>
          <EmbedInput
            id={`${effectiveVariant}-city`}
            name="city"
            autoComplete="address-level2"
            type="text"
            value={fields.city}
            onChange={(e) => updateField("city", e.target.value)}
            aria-required="true"
            invalid={!!errors.city}
            aria-invalid={!!errors.city}
          />
        </EmbedField>
        {isDomestic ? (
          <EmbedField label="State" htmlFor={`${effectiveVariant}-state`} required error={errors.state}>
            {/* address-level1 is valid on a select — browsers honor the token
                on the dropdown just as on a free-text region input. */}
            <EmbedSelect
              id={`${effectiveVariant}-state`}
              name="state"
              autoComplete="address-level1"
              value={fields.state}
              onChange={(e) => updateField("state", e.target.value)}
              aria-required="true"
              invalid={!!errors.state}
              aria-invalid={!!errors.state}
            >
              <option value="">Select state</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </EmbedSelect>
          </EmbedField>
        ) : (
          <EmbedField
            label="State / Province / Region"
            htmlFor={`${effectiveVariant}-state`}
            required
            error={errors.state}
            help={`Up to ${STATE_MAX_LEN} characters.`}
          >
            <EmbedInput
              id={`${effectiveVariant}-state`}
              name="state"
              autoComplete="address-level1"
              type="text"
              value={fields.state}
              onChange={(e) => updateField("state", e.target.value)}
              maxLength={STATE_MAX_LEN}
              aria-required="true"
              invalid={!!errors.state}
              aria-invalid={!!errors.state}
            />
          </EmbedField>
        )}

        <EmbedField
          label={isDomestic ? "ZIP code" : "Postal code"}
          htmlFor={`${effectiveVariant}-zip`}
          required
          error={errors.postal_code}
        >
          <EmbedInput
            id={`${effectiveVariant}-zip`}
            name="postal_code"
            autoComplete="postal-code"
            type="text"
            {...(isDomestic ? { inputMode: "numeric" as const } : {})}
            value={fields.postal_code}
            onChange={(e) => updateField("postal_code", e.target.value)}
            aria-required="true"
            invalid={!!errors.postal_code}
            aria-invalid={!!errors.postal_code}
          />
        </EmbedField>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg }}>
        {onBack && (
          <EmbedButton type="button" variant="secondary" onClick={onBack}>
            {isEditing ? "Cancel" : "Back"}
          </EmbedButton>
        )}
        <EmbedButton type="submit" variant="primary" disabled={submitting} loading={submitting}>
          {submitting ? "Saving..." : isEditing ? "Save changes" : "Continue"}
        </EmbedButton>
      </div>
    </form>
  );
}
